import {
  each, filter, uniq, findIndex, groupBy, orderBy, concat, find, map,
  pick, eachRight, camelCase, reduce, maxBy, values, last, first, cloneDeep,
} from 'lodash';
import statsJs from 'statsjs';
import moment from 'moment';
import {
  FETCH_PIPELINE_STATS,
  FETCH_PIPELINE_CONVERSION_OPPORTUNITIES,
} from './PipelineActions';
import { ADD_ANALYSIS_OPPORTUNITY_LOGS } from '../Analysic/AnalysisActions';
import { DASHBOARD_FETCH_WIN_LOSS_OPPORTUNITY_REQUESTED_FEEDBACK } from '../Dashboard/DashboardActions';


// Initial State
const initialState = {
  stats: {},
  status: null,
  errors: null,
  opportunities: [],
  stages: [],
  opportunityAmounts: [],
  defaultAmountBounds: [],
  brenchmarkGraph: {
    average: [], // [ { stage 1}, { stage 2} ]
    owners: {}, // { ownerId: [{ stage 1}, { stage 2}], ownerId: [{stage 1}, {stage 2}] }
  },
  historyGraphs: {
    status: null,
    errors: null,
    opportunities: [],
    average: [], // [ { stage 1}, { stage 2} ]
    owners: {}, // { ownerId: [{ stage 1}, { stage 2}], ownerId: [{stage 1}, {stage 2}] }
    bestInClassCount: null,
    bestInClassAmount: null,
    historicalSales: {
      global: {},
      owners: {},
    },
  },
  velocityGraph: {
    velocityPipeline: {},
    velocityOwners: {},
  },
  projectedGraph: {
    globalStats: {},
    ownerStats: {},
  },
};

export const getStatsFromOpportunities = (opportunities, cb = () => {}) => {
  const oneStats = {
    count: 0,
    amount: 0,
    ids: [],

    netChangeIncreaseAmount: 0,
    netChangeIncreaseCount: 0,
    netChangeIncreaseIds: [],

    netChangeDecreaseAmount: 0,
    netChangeDecreaseCount: 0,
    netChangeDecreaseIds: [],

    netGrowthNewAmount: 0,
    netGrowthNewCount: 0,
    netGrowthNewIds: [],

    netGrowthClosedAmount: 0,
    netGrowthClosedCount: 0,
    netGrowthClosedIds: [],

    netAdvancedAmount: 0,
    netAdvancedCount: 0,
    netAdvancedIds: [],

    netRegressedAmount: 0,
    netRegressedCount: 0,
    netRegressedIds: [],

    delayedAmount: 0,
    delayedCount: 0,
    deltaCloseDate: 0,
  };

  const totalStats = cloneDeep(oneStats);
  const indexedOwnerStats = {}; // indexed by owner id

  each(opportunities, (opportunity) => {
    if (cb) {
      cb(opportunity);
    }
    const opportunityId = opportunity.opportunity_id;
    const amt = opportunity.amount || 0;
    if (!indexedOwnerStats[opportunity.owner_id]) {
      indexedOwnerStats[opportunity.owner_id] = cloneDeep(oneStats);
    }
    const ownerStats = indexedOwnerStats[opportunity.owner_id];

    if (!opportunity.is_closed) {
      // open
      totalStats.count += 1;
      totalStats.amount += amt;
      totalStats.ids.push(opportunityId);
      ownerStats.count += 1;
      ownerStats.amount += amt;
      if (!opportunity.is_new) {
        if (opportunity.delta_amount > 0) {
          totalStats.netChangeIncreaseAmount += opportunity.delta_amount;
          totalStats.netChangeIncreaseCount += 1;
          totalStats.netChangeIncreaseIds.push(opportunityId);
          ownerStats.netChangeIncreaseAmount += opportunity.delta_amount;
          ownerStats.netChangeIncreaseCount += 1;
        } else if (opportunity.delta_amount < 0) {
          totalStats.netChangeDecreaseAmount += (-1 * opportunity.delta_amount);
          totalStats.netChangeDecreaseCount += 1;
          totalStats.netChangeDecreaseIds.push(opportunityId);
          ownerStats.netChangeDecreaseAmount += (-1 * opportunity.delta_amount);
          ownerStats.netChangeDecreaseCount += 1;
        }
        if (opportunity.delta_stage_order > 0) {
          totalStats.netAdvancedAmount += amt;
          totalStats.netAdvancedCount += 1;
          totalStats.netAdvancedIds.push(opportunityId);
          ownerStats.netAdvancedAmount += amt;
          ownerStats.netAdvancedCount += 1;
        } else if (opportunity.delta_stage_order < 0) {
          totalStats.netRegressedAmount += amt;
          totalStats.netRegressedCount += 1;
          totalStats.netRegressedIds.push(opportunityId);
          ownerStats.netRegressedAmount += amt;
          ownerStats.netRegressedCount += 1;
        }
        if (opportunity.delta_close_date > 0) {
          totalStats.delayedAmount += amt;
          totalStats.delayedCount += 1;
          totalStats.deltaCloseDate += opportunity.delta_close_date;
          ownerStats.delayedAmount += amt;
          ownerStats.delayedCount += 1;
          ownerStats.deltaCloseDate += opportunity.delta_close_date;
        }
      } else {
        // is new
        totalStats.netGrowthNewAmount += amt;
        totalStats.netGrowthNewCount += 1;
        totalStats.netGrowthNewIds.push(opportunityId);
        ownerStats.netGrowthNewAmount += amt;
        ownerStats.netGrowthNewCount += 1;
      }
    } else if (opportunity.is_closed && opportunity.close_after_pipeline) {
      // closed
      totalStats.netGrowthClosedAmount += amt;
      totalStats.netGrowthClosedCount += 1;
      totalStats.netGrowthClosedIds.push(opportunityId);
      ownerStats.netGrowthClosedAmount += amt;
      ownerStats.netGrowthClosedCount += 1;
    }
  });
  totalStats.average = totalStats.count !== 0 ? totalStats.amount / totalStats.count : 0;
  totalStats.delayed_average = totalStats.delayedCount !== 0 ? totalStats.deltaCloseDate / totalStats.delayedCount : 0;

  return {
    totalStats,
    indexedOwnerStats,
  };
};


function calculateAdvanced(stages) {
  let totalCount = 0;
  let totalAmount = 0;
  each(stages, (stage) => {
    totalCount += stage.wonCount + stage.lostCount + stage.noDecisionCount;
    totalAmount += stage.wonAmount + stage.lostAmount + stage.noDecisionAmount;
  });
  let currentLostCount = 0;
  let currentLostAmount = 0;

  each(stages, (stage) => {
    if (!stage.skip_conversion) {
      currentLostCount += stage.lostCount + stage.noDecisionCount;
      currentLostAmount += stage.lostAmount + stage.noDecisionAmount;
      stage.advancedCount = totalCount - currentLostCount;
      stage.advancedAmount = totalAmount - currentLostAmount;
    }
  });

  each(stages, (stage) => {
    if (!stage.skip_conversion) {
      const sumCount = stage.lostCount + stage.noDecisionCount + stage.advancedCount;
      const sumAmount = stage.lostAmount + stage.noDecisionAmount + stage.advancedAmount;
      each(['Won', 'Lost', 'No Decision'], (s) => {
        let count = stage[`${camelCase(`${s} Count`)}`];
        let amount = stage[`${camelCase(`${s} Amount`)}`];
        if (s === 'Won') {
          count = stage.advancedCount;
          amount = stage.advancedAmount;
        }
        stage[`${camelCase(`${s} Count Rate`)}`] = sumCount ? count / sumCount : 0;
        stage[`${camelCase(`${s} Amount Rate`)}`] = sumAmount ? amount / sumAmount : 0;
      });
    }
  });

  let currentProbabilityRateAmount = 1;
  let currentProbabilityRateCount = 1;
  let advancedOwnerIds = [];
  eachRight(stages, (stage) => {
    if (!stage.skip_conversion) {
      stage.amountProbabilityRate = currentProbabilityRateAmount * stage.wonAmountRate;
      stage.countProbabilityRate = currentProbabilityRateCount * stage.wonCountRate;
      advancedOwnerIds = uniq(concat(advancedOwnerIds, stage.convertedOwnerIds, stage.lostOwnerIds));
      currentProbabilityRateAmount = stage.amountProbabilityRate;
      currentProbabilityRateCount = stage.countProbabilityRate;
      stage.advancedOwnerIds = advancedOwnerIds;
    }
  });
}
function getProbabilityRate(stages) {
  let winCountProbabilityRate = 1;
  let winAmountProbabilityRate = 1;
  each(stages, (stage) => {
    if (!stage.skip_conversion) {
      winCountProbabilityRate *= stage.wonCountRate; // count conversion rate
      winAmountProbabilityRate *= stage.wonAmountRate; // amount conversion rate
    }
  });
  return {
    winCountProbabilityRate,
    winAmountProbabilityRate,
  };
}

function calculateConversions(stages, opportunities, cb) {
  // should return { average: [], owners: { ownerId: [], ownerId: [] } }

  let sOrdr = 1;
  const stageOrders = reduce(stages, (items, stage) => {
    sOrdr *= 10;
    items[stage.value] = sOrdr;
    return items;
  }, {});
  const getFurthestStage = inputs => last(orderBy(inputs, [input => stageOrders[input]], ['asc']));

  const stageFields = ['label', 'value', 'conversion_stage', 'skip_conversion'];
  const owners = {};
  const groupedByOwnerId = groupBy(opportunities, 'owner_id');
  const indexedAverageStages = {};
  const convertedTypeStats = {}; // across all stages
  const convertedIds = []; // across all stages
  const convertedOwnerIds = [];
  let convertedCount = 0;
  let convertedAmount = 0;
  const average = stages.map((s) => {
    const stage = {
      ...pick(s, stageFields),
      wonAmount: 0,
      wonCount: 0,
      lostAmount: 0,
      lostCount: 0,
      noDecisionAmount: 0,
      noDecisionCount: 0,
      wonIds: [],
      lostIds: [],
      noDecisionIds: [],
      advancedCount: 0,
      advancedAmount: 0,
      wonTypeStats: {},
      lostTypeStats: {},
      noDecisionTypeStats: {},
      convertedTypeStats, // reference!
      convertedIds, // reference!
      convertedAmount,
      convertedCount,
      lostOwnerIds: [],
      convertedOwnerIds, // reference!
      advancedOwnerIds: [], // for calculate average PER stage!
    };
    indexedAverageStages[s.value] = stage;
    return stage;
  });
  if (stages.length === 0) {
    return {
      owners,
      average,
    };
  }
  const bestInClass = {};


  each(groupedByOwnerId, (ownerOpportunities, ownerId) => {
    const indexedStages = {};
    const ownerStages = stages.map((s) => {
      const stage = {
        ...pick(s, stageFields),
        wonAmount: 0,
        wonCount: 0,
        lostAmount: 0,
        lostCount: 0,
        noDecisionAmount: 0,
        noDecisionCount: 0,
        wonIds: [],
        lostIds: [],
        noDecisionIds: [],
        advancedCount: 0,
        advancedAmount: 0,
        wonTypeStats: {},
        lostTypeStats: {},
        convertedAmount,
        convertedCount,
        noDecisionTypeStats: {},
      };
      indexedStages[s.value] = stage;
      return stage;
    });
    if (!bestInClass[ownerId]) {
      bestInClass[ownerId] = {
        count: 0,
        amount: 0,
        ownerId,
        wonCount: 0,
        lostCount: 0,
        noDecisionCount: 0,
        wonAmount: 0,
        lostAmount: 0,
        noDecisionAmount: 0,
      };
    }
    each(ownerOpportunities, (opportunity) => {
      const lastOpenStage = getFurthestStage(opportunity.stage_histories || []);
      const targetStage = lastOpenStage && indexedStages[lastOpenStage] ? indexedStages[lastOpenStage] : ownerStages[ownerStages.length - 1];
      const targetAverageStage = lastOpenStage && indexedAverageStages[lastOpenStage] ? indexedAverageStages[lastOpenStage] : average[average.length - 1];
      const { stage } = opportunity;
      let { amount } = opportunity;
      if (amount === 0) {
        amount = 1;
      }
      bestInClass[ownerId].count += 1;
      bestInClass[ownerId].amount += amount;
      cb(opportunity); // assign owner
      if (targetStage) {
        if (!targetStage.skip_conversion) {
          const id = opportunity.opportunity_id;

          let typeKey = null;
          if (stage === 'Won') {
            targetStage.wonCount += 1;
            targetStage.wonAmount += amount;
            convertedCount += 1;
            convertedAmount += amount;
            targetStage.wonIds.push(id);
            targetAverageStage.wonCount += 1;
            targetAverageStage.wonAmount += amount;
            targetAverageStage.wonIds.push(id);
            convertedIds.push(id);
            // Best-in-Class = the sales rep that has sold the most over the past 12 months based on the current date
            // only won?
            bestInClass[ownerId].wonCount += 1;
            bestInClass[ownerId].wonAmount += amount;
            convertedOwnerIds.push(ownerId);
            typeKey = 'wonTypeStats';
          } else if (stage === 'Lost') {
            targetStage.lostCount += 1;
            targetStage.lostAmount += amount;
            targetStage.lostIds.push(id);
            targetAverageStage.lostCount += 1;
            targetAverageStage.lostAmount += amount;
            targetAverageStage.lostIds.push(id);
            bestInClass[ownerId].lostCount += 1;
            bestInClass[ownerId].lostAmount += amount;
            targetAverageStage.lostOwnerIds.push(ownerId);
            typeKey = 'lostTypeStats';
          } else if (stage === 'No Decision') {
            targetStage.noDecisionCount += 1;
            targetStage.noDecisionAmount += amount;
            targetStage.noDecisionIds.push(id);
            targetAverageStage.noDecisionCount += 1;
            targetAverageStage.noDecisionAmount += amount;
            targetAverageStage.noDecisionIds.push(id);
            bestInClass[ownerId].noDecisionCount += 1;
            bestInClass[ownerId].noDecisionAmount += amount;
            targetAverageStage.lostOwnerIds.push(ownerId);
            typeKey = 'noDecisionTypeStats';
          }
          each(opportunity.types, (dealType) => {
            if (typeKey) {
              each([targetStage, targetAverageStage], (s) => {
                if (!s[typeKey][dealType]) {
                  s[typeKey][dealType] = { count: 0, amount: 0 };
                }
                s[typeKey][dealType].count += 1;
                s[typeKey][dealType].amount += amount;
              });
            }
            if (stage === 'Won') {
              if (!convertedTypeStats[dealType]) {
                convertedTypeStats[dealType] = { count: 0, amount: 0 };
              }
              convertedTypeStats[dealType].count += 1;
              convertedTypeStats[dealType].amount += amount;
            }
          });
        }
      }
    });

    calculateAdvanced(ownerStages);
    each(ownerStages, (ownerStage) => {
      const targetAverageStage = indexedAverageStages[ownerStage.value] ? indexedAverageStages[ownerStage.value] : average[average.length - 1];
      if (targetAverageStage) {
        each(['Won', 'Lost', 'No Decision'], (s) => {
          each(['Amount', 'Count'], (type) => {
            const k = `${camelCase(`sum ${s} ${type} Rate`)}`;
            if (!targetAverageStage[k]) {
              targetAverageStage[k] = 0;
            }
            targetAverageStage[k] += ownerStage[`${camelCase(`${s} ${type} Rate`)}`];
          });
        });
      }
    });
    owners[ownerId] = {
      ...ownerOpportunities[0].owner,
      stages: ownerStages,
      ...getProbabilityRate(ownerStages),
    };
  });

  calculateAdvanced(average);

  each(average, (stage) => {
    const totalOwners = stage.advancedOwnerIds.length;
    stage.convertedAmount = convertedAmount;
    stage.convertedCount = convertedCount;
    if (!stage.skip_conversion) {
      // average
      each(['Won', 'Lost', 'No Decision'], (s) => {
        stage[`${camelCase(`average ${s} Count Rate`)}`] = totalOwners ? stage[`${camelCase(`sum ${s} Count Rate`)}`] / totalOwners : 0;
        stage[`${camelCase(`average ${s} Amount Rate`)}`] = totalOwners ? stage[`${camelCase(`sum ${s} Amount Rate`)}`] / totalOwners : 0;

        stage[`${camelCase(`average ${s} Amount`)}`] = totalOwners ? stage[`${camelCase(`${s} Amount`)}`] / totalOwners : 0;
        stage[`${camelCase(`average ${s} Count`)}`] = totalOwners ? stage[`${camelCase(`${s} Count`)}`] / totalOwners : 0;
      });
    }
  });
  let averageAmountProbabilityRate = 1;
  let averageCountProbabilityRate = 1;
  eachRight(average, (stage) => {
    if (!stage.skip_conversion) {
      // average
      stage.averageAmountProbabilityRate = averageAmountProbabilityRate * stage.averageWonAmountRate;
      stage.averageCountProbabilityRate = averageCountProbabilityRate * stage.averageWonCountRate;
      ({ averageAmountProbabilityRate, averageCountProbabilityRate } = stage);
    }
  });

  const allBest = values(bestInClass);
  const bestInClassCount = maxBy(allBest, o => o.wonCount);
  const bestInClassAmount = maxBy(allBest, o => o.wonAmount);
  each(owners, (owner, ownerId) => {
    const stats = bestInClass[ownerId] || {};
    each(['Won', 'Lost', 'No Decision'], (s) => {
      stats[`${camelCase(`${s} Count Rate`)}`] = stats.count ? stats[`${camelCase(`${s} Count`)}`] / stats.count : 0;
      stats[`${camelCase(`${s} Amount Rate`)}`] = stats.amount ? stats[`${camelCase(`${s} Amount`)}`] / stats.amount : 0;
    });
    owners[ownerId] = { ...owner, stats };
  });
  return {
    average,
    owners,
    bestInClassCount,
    bestInClassAmount,
  };
}

export function conversionGraphReducer(action, stages, conversionGraph) {
  const {
    opportunities, owners, accounts, status, errors,
  } = action;
  const historicalSales = {
    global: {}, // indexed by date index 'MM-YYYY'
    owners: {}, // indexed by owner id
  };
  const conversions = calculateConversions(stages, opportunities, (opportunity) => {
    // make this a callback so we don't need to loop thru all opportunities twice
    const ownerId = opportunity.owner_id;
    opportunity.owner = owners[ownerId];
    opportunity.account = accounts[opportunity.account_id];
    const closeDate = opportunity.close_date ? moment(opportunity.close_date, 'YYYY-MM-DD') : null;
    const createdDate = opportunity.created_date ? moment(opportunity.created_date, 'YYYY-MM-DD') : null;
    opportunity.close_date = closeDate;
    opportunity.created_date = createdDate;
    if (closeDate) {
      const dateIndex = closeDate.format('MM-YYYY');
      if (typeof historicalSales.global[dateIndex] === 'undefined') {
        historicalSales.global[dateIndex] = 0;
      }
      historicalSales.global[dateIndex] += opportunity.amount || 0;

      if (typeof historicalSales.owners[ownerId] === 'undefined') {
        historicalSales.owners[ownerId] = {};
      }
      if (typeof historicalSales.owners[ownerId][dateIndex] === 'undefined') {
        historicalSales.owners[ownerId][dateIndex] = 0;
      }
      historicalSales.owners[ownerId][dateIndex] += opportunity.amount || 0;
    }
  });
  return {
    ...conversionGraph,
    opportunities,
    status,
    errors,
    historicalSales,
    ...conversions,
  };
}

function velocityReducer(velocityPipeline, velocityOwners, opportunity, opportunityTypeStats) {
  const salesVelocity = opportunity.sales_velocity;
  const stageVelocity = opportunity.stage_velocity;
  const stageName = opportunity.stage_name;
  const { amount, owner } = opportunity;
  const id = opportunity.opportunity_id;
  const ownerId = opportunity.owner_id;

  if (!velocityPipeline.categories[salesVelocity]) {
    velocityPipeline.categories[salesVelocity] = {
      count: 0, amount: 0, ids: [], amountPercentage: 0, countPercentage: 0, stats: {},
    };
  }
  if (!velocityOwners[ownerId]) {
    velocityOwners[ownerId] = {
      ...owner,
      count: 0,
      amount: 0,
      categories: {
        // accelerated, onTrack, lagging
      },
      stages: {},
    };
  }
  if (!velocityOwners[ownerId].categories[salesVelocity]) {
    velocityOwners[ownerId].categories[salesVelocity] = {
      count: 0, amount: 0, ids: [], amountPercentage: 0, countPercentage: 0, stats: {},
    };
  }

  if (!velocityPipeline.stages[stageName]) {
    velocityPipeline.stages[stageName] = {
      count: 0, amount: 0, ownerIds: [], categories: { /* recent, working, stalled */ },
    };
  }
  if (!velocityPipeline.stages[stageName].categories[stageVelocity]) {
    velocityPipeline.stages[stageName].categories[stageVelocity] = {
      count: 0, amount: 0, ids: [], amountPercentage: 0, countPercentage: 0, stats: {},
    };
  }
  if (!velocityOwners[ownerId].stages[stageName]) {
    velocityOwners[ownerId].stages[stageName] = { count: 0, amount: 0, categories: { /* recent, working, stalled */ } };
  }
  if (!velocityOwners[ownerId].stages[stageName].categories[stageVelocity]) {
    velocityOwners[ownerId].stages[stageName].categories[stageVelocity] = {
      count: 0, amount: 0, ids: [], amountPercentage: 0, countPercentage: 0, stats: {},
    };
  }

  const globalPipelineCategory = velocityPipeline.categories[salesVelocity];
  const ownerPipelineCategory = velocityOwners[ownerId].categories[salesVelocity];
  const globalStage = velocityPipeline.stages[stageName];
  const globalStageCategory = velocityPipeline.stages[stageName].categories[stageVelocity];
  const ownerStage = velocityOwners[ownerId].stages[stageName];
  const ownerStageCategory = velocityOwners[ownerId].stages[stageName].categories[stageVelocity];

  globalStage.ownerIds = uniq(concat(globalStage.ownerIds, [ownerId]));

  each([globalPipelineCategory, ownerPipelineCategory, globalStageCategory, ownerStageCategory], (category) => {
    category.count += 1;
    category.amount += amount;
    category.ids.push(id);
  });

  each([velocityPipeline, velocityOwners[ownerId], globalStage, ownerStage], (category) => {
    category.count += 1;
    category.amount += amount;
    each(category.categories, (subCategory) => {
      if (category.count !== 0) {
        subCategory.countPercentage = subCategory.count / category.count;
      }
      if (category.amount !== 0) {
        subCategory.amountPercentage = subCategory.amount / category.amount;
      }
    });
  });

  each(opportunityTypeStats, (stats, dealType) => {
    each([ownerStageCategory, globalStageCategory, ownerPipelineCategory, globalPipelineCategory], (category) => {
      if (!category.stats[dealType]) {
        category.stats[dealType] = { count: 0, amount: 0 };
      }
      category.stats[dealType].count += stats.count;
      category.stats[dealType].amount += stats.amount;
    });
  });
}

function makeProjectedStatsFuncs(stages, q3) {
  const globalProjectedStats = {}; // indexed by months ('01-2019', '02-2019')
  const ownerProjectedStats = {}; // by owner id, then by months

  const oneStats = {
    totalOpportunities: [],
    totalAmount: 0,

    oldAmount: 0,
    oldAmountPercentage: 0,
    oldOpportunities: [],

    bigAmount: 0,
    bigAmountPercentage: 0,
    bigOpportunities: [],

    stages: {

    }, // indexed by stage
  };

  function calculateProjected(opportunity) {
    const { amount } = opportunity;
    const stageName = opportunity.stage_name;
    const ownerId = opportunity.owner_id;
    const closeDate = opportunity.close_date;
    const closeDateIndexed = closeDate.format('MM-YYYY');
    const stage = find(stages, { value: stageName });
    if (!stage) {
      return;
    }
    if (!globalProjectedStats[closeDateIndexed]) {
      globalProjectedStats[closeDateIndexed] = cloneDeep(oneStats);
    }
    if (!ownerProjectedStats[ownerId]) {
      ownerProjectedStats[ownerId] = {};
    }
    if (!ownerProjectedStats[ownerId][closeDateIndexed]) {
      ownerProjectedStats[ownerId][closeDateIndexed] = cloneDeep(oneStats);
    }
    const globalStats = globalProjectedStats[closeDateIndexed];
    const ownerStats = ownerProjectedStats[ownerId][closeDateIndexed];
    const isBig = amount >= q3;
    const isOld = !opportunity.is_stage_new;
    // Days Left in Current Stage = Average Days in Sales Stage for Wins - Count of Days Opportunity has been in Current Stage
    opportunity.days_left_in_current_stage = stage.average_age - opportunity.days_in_stage;
    // Days to Close = Days Left in Current Stage + Days for Future Sales Stages
    opportunity.days_to_close = opportunity.days_left_in_current_stage + stage.future_average_age;

    let globalStageStats = globalStats.stages[stageName];
    if (!globalStageStats) {
      globalStageStats = cloneDeep(oneStats);
      globalStageStats.skip = stage.skip_conversion;
      delete globalStageStats.stages;
      globalStats.stages[stageName] = globalStageStats;
    }
    let ownerStageStats = ownerStats.stages[stageName];
    if (!ownerStageStats) {
      ownerStageStats = cloneDeep(oneStats);
      ownerStageStats.skip = stage.skip_conversion;
      delete ownerStageStats.stages;
      ownerStats.stages[stageName] = ownerStageStats;
    }

    each([globalStats, ownerStats, globalStageStats, ownerStageStats], (stats) => {
      stats.totalOpportunities.push(opportunity);
      stats.totalAmount += amount;
      if (isBig) {
        stats.bigOpportunities.push(opportunity);
        stats.bigAmount += amount;
      }
      if (isOld) {
        stats.oldOpportunities.push(opportunity);
        stats.oldAmount += amount;
      }
    });
  }

  return {
    calculateProjected,
    globalProjectedStats,
    ownerProjectedStats,
  };
}

// days required to close a deal
function calculateFutureCloseDay(stages) {
  let currentAge = 0;
  eachRight(stages, (stage) => {
    stage.future_average_age = currentAge;
    if (!stage.skip_conversion) {
      currentAge += stage.average_age;
    }
  });
  return stages;
}

function pipelineReducer(action) {
  const {
    opportunities, stages, owners, status, errors,
  } = action;
  const stageFields = ['value', 'label', 'conversion_stage', 'skip_conversion'];

  const indexedAverageStages = {};
  const average = stages.map((s) => {
    const stage = {
      ...pick(s, stageFields),
      totalTypeStats: {},
      totalAmount: 0,
    };
    indexedAverageStages[s.value] = stage;
    return stage;
  });

  const velocityPipeline = {
    count: 0,
    amount: 0,
    categories: {
      // accelerated, onTrack, lagging
    },
    stages: {},
  };
  const velocityOwners = {};
  const openOpportunities = filter(opportunities, { is_closed: false });

  const amounts = uniq(map(opportunities, 'amount').sort((a, b) => (a - b)));
  const stats = statsJs(amounts);
  const q3 = stats.q3();
  calculateFutureCloseDay(stages);
  const {
    calculateProjected,
    globalProjectedStats, ownerProjectedStats,
  } = makeProjectedStatsFuncs(stages, q3);
  each(opportunities, (opportunity) => {
    if (owners[opportunity.owner_id]) {
      opportunity.owner = owners[opportunity.owner_id];
    }
    const closeDate = opportunity.close_date ? moment(opportunity.close_date, 'YYYY-MM-DD') : null;
    const createdDate = opportunity.created_date ? moment(opportunity.created_date, 'YYYY-MM-DD') : null;
    opportunity.close_date = closeDate;
    opportunity.created_date = createdDate;
    const { amount } = opportunity;
    calculateProjected(opportunity);
    if (owners[opportunity.owner_id]) {
      const owner = owners[opportunity.owner_id];
      if (!owner.stages) {
        owner.indexedStages = {};
        owner.stages = stages.map((s) => {
          const stage = {
            ...pick(s, stageFields),
            totalTypeStats: {},
            totalAmount: 0,
          };
          owner.indexedStages[s.value] = stage;
          return stage;
        });
      }
      const stageName = opportunity.stage_name;
      const id = opportunity.opportunity_id;
      const typeStats = [];
      if (indexedAverageStages[stageName]) {
        typeStats.push(indexedAverageStages[stageName].totalTypeStats);
        indexedAverageStages[stageName].totalAmount += amount;
      }
      if (owner.indexedStages[stageName]) {
        typeStats.push(owner.indexedStages[stageName].totalTypeStats);
        owner.indexedStages[stageName].totalAmount += amount;
      }
      const opportunityTypeStats = {};
      each(opportunity.types, (dealType) => {
        if (!opportunityTypeStats[dealType]) {
          opportunityTypeStats[dealType] = { count: 0, amount: 0, ids: [] };
        }
        opportunityTypeStats[dealType].count += 1;
        opportunityTypeStats[dealType].amount += amount;
        opportunityTypeStats[dealType].ids.push(id);
        each(typeStats, (s) => {
          if (!s[dealType]) {
            s[dealType] = { count: 0, amount: 0, ids: [] };
          }
          s[dealType].count += 1;
          s[dealType].amount += amount;
          s[dealType].ids.push(id);
        });
      });
      velocityReducer(velocityPipeline, velocityOwners, opportunity, opportunityTypeStats);
    }
  });

  const lowerIndex = Math.floor((amounts.length / 3) - 1);
  const upperIndex = Math.floor((2 * (amounts.length / 3)) - 1);
  const defaultAmountBounds = [lowerIndex, upperIndex];

  // "average" meaning we need to devide the numbers by total sales reps
  const totalOwners = values(owners).length;
  each(average, (avg) => {
    each(avg.totalTypeStats, (typeStat) => {
      typeStat.count = totalOwners !== 0 ? typeStat.count / totalOwners : 0;
      typeStat.amount = totalOwners !== 0 ? typeStat.amount / totalOwners : 0;
    });
  });


  return {
    // stats: getStatsFromOpportunities(openOpportunities).totalStats,
    stats: getStatsFromOpportunities(opportunities).totalStats,
    opportunities: openOpportunities,
    stages,
    opportunityAmounts: amounts,
    defaultAmountBounds,
    status,
    errors,
    brenchmarkGraph: {
      average,
      owners,
    },
    velocityGraph: {
      velocityPipeline,
      velocityOwners,
    },
    projectedGraph: {
      globalStats: globalProjectedStats,
      ownerStats: ownerProjectedStats,
    },
  };
}

export function calculateProjectedSales(results, pipelineStages, isAverage = true) {
  // prroject sales based on pipeline amount
  // and historical rate
  const firstStage = first(results);
  let firstProbabilityRate = 0;
  let countFirstProbabilityRate = 0;
  if (firstStage) {
    if (isAverage) {
      firstProbabilityRate = firstStage.averageAmountProbabilityRate;
      countFirstProbabilityRate = firstStage.averageCountProbabilityRate;
    } else {
      firstProbabilityRate = firstStage.amountProbabilityRate;
      countFirstProbabilityRate = firstStage.countProbabilityRate;
    }
  }

  let earlyProjectedSales = 0;
  let lateProjectedSales = 0;
  let countEarlyProjectedSales = 0;
  let countLateProjectedSales = 0;
  each(results, (result) => {
    // const projectedSales = result.amountProbabilityRate * (result.wonAmount + result.lostAmount + result.noDecisionAmount);
    // USE THE AVERAGE RATE
    const pipelineStage = find(pipelineStages, { value: result.value });
    if (pipelineStage) {
      const rate = isAverage ? result.averageAmountProbabilityRate || 0 : result.amountProbabilityRate || 0;
      const countRate = isAverage ? result.averageCountProbabilityRate || 0 : result.countProbabilityRate || 0;

      const projectedSales = rate * (pipelineStage.totalAmount);
      const countProjectedSales = countRate * (pipelineStage.totalCount);

      if (result.conversion_stage === 'early') {
        earlyProjectedSales += projectedSales;
        countEarlyProjectedSales += countProjectedSales;
      } else if (result.conversion_stage === 'late') {
        lateProjectedSales += projectedSales;
        countLateProjectedSales += countProjectedSales;
      }
    }
  });

  return {
    totalProjectedSales: earlyProjectedSales + lateProjectedSales,
    earlyProjectedSales,
    lateProjectedSales,
    firstProbabilityRate,
    countFirstProbabilityRate,
    countLateProjectedSales,
    countEarlyProjectedSales,
    countTotalProjectedSales: countLateProjectedSales + countEarlyProjectedSales,
  };
}

function calculateProjectedGraph(projectedGraph, stages, ownerStages) {
  function calculate(stats, rates) {
    return reduce(stats, (items, s, dateIndex) => {
      let totalProjectedSales = 0;
      let bigProjectedSales = 0;
      let oldProjectedSales = 0;

      const statStages = reduce(s.stages, (itms, stage, stageValue) => {
        const rate = rates[stageValue];
        const newStage = {
          ...stage,
          totalProjectedSales: stage.totalAmount * rate,
          bigProjectedSales: stage.bigAmount * rate,
          oldProjectedSales: stage.oldAmount * rate,
          bigAmountPercentage: stage.totalAmount ? stage.bigAmount / stage.totalAmount : 0,
          oldAmountPercentage: stage.totalAmount ? stage.oldAmount / stage.totalAmount : 0,
        };
        itms[stageValue] = newStage;
        if (!stage.skip) {
          totalProjectedSales += newStage.totalProjectedSales;
          bigProjectedSales += newStage.bigProjectedSales;
          oldProjectedSales += newStage.oldProjectedSales;
        }
        return itms;
      }, {});

      items[dateIndex] = {
        ...s,
        totalProjectedSales,
        bigProjectedSales,
        oldProjectedSales,
        stages: statStages,
        bigAmountPercentage: s.totalAmount !== 0 ? s.bigAmount / s.totalAmount : 0,
        oldAmountPercentage: s.totalAmount !== 0 ? s.oldAmount / s.totalAmount : 0,
        bigProjectedSalesPercentage: totalProjectedSales !== 0 ? bigProjectedSales / totalProjectedSales : 0,
        oldProjectedSalesPercentage: totalProjectedSales !== 0 ? oldProjectedSales / totalProjectedSales : 0,
      };
      return items;
    }, {});
  }

  // global average rates
  const indexedWinProbabilityRate = reduce(stages, (items, stage) => {
    items[stage.value] = stage.averageAmountProbabilityRate; // or stage.amountProbabilityRate?
    return items;
  }, {});

  return {
    globalStats: calculate(projectedGraph.globalStats, indexedWinProbabilityRate),
    ownerStats: reduce(projectedGraph.ownerStats, (items, stats, ownerId) => {
      // owner rates
      const rates = reduce(ownerStages[ownerId] ? ownerStages[ownerId].stages : [], (itms, stage) => {
        itms[stage.value] = stage.amountProbabilityRate;
        return itms;
      }, {});
      items[ownerId] = calculate(stats, rates);
      return items;
    }, {}),
  };
}

const PipelineReducer = (state = initialState, action) => {
  switch (action.type) {
    case FETCH_PIPELINE_STATS: {
      return {
        ...state,
        ...pipelineReducer(action),
      };
    }
    case ADD_ANALYSIS_OPPORTUNITY_LOGS: {
      const { opportunities, historyGraphs } = state;
      const { status, logDetails, opportunityId } = action;
      const idx = findIndex(opportunities, { opportunity_id: opportunityId });
      const newCollection = opportunities;
      if (idx >= 0) {
        newCollection[idx] = { ...newCollection[idx], $status: status, $logDetails: logDetails };
      }

      const newConversionCollection = historyGraphs.opportunities;
      const conversionIdx = findIndex(historyGraphs.opportunities, { opportunity_id: opportunityId });
      if (conversionIdx >= 0) {
        newConversionCollection[conversionIdx] = { ...newConversionCollection[conversionIdx], $status: status, $logDetails: logDetails };
      }
      return { ...state, opportunities: newCollection, historyGraphs: { ...historyGraphs, opportunities: newConversionCollection } };
    }
    case FETCH_PIPELINE_CONVERSION_OPPORTUNITIES: {
      const { stats, brenchmarkGraph } = state;
      const historyGraphs = conversionGraphReducer(action, state.stages, state.historyGraphs);
      const projectedSales = calculateProjectedSales(
        filter(historyGraphs.average, { skip_conversion: false }),
        brenchmarkGraph.average, // array of stages
      );

      const projectedGraph = calculateProjectedGraph(state.projectedGraph, historyGraphs.average, historyGraphs.owners);

      return {
        ...state,
        projectedGraph,
        stats: { ...stats, ...projectedSales },
        historyGraphs,
      };
    }
    case DASHBOARD_FETCH_WIN_LOSS_OPPORTUNITY_REQUESTED_FEEDBACK: {
      const { total, id } = action;
      const opportunities = reduce(state.opportunities, (items, opportunity) => {
        if (opportunity.opportunity_id === id) {
          items.push({
            ...opportunity,
            requested_feedbacks: total,
          });
        } else {
          items.push(opportunity);
        }
        return items;
      }, []);
      return {
        ...state,
        opportunities,
      };
    }
    default:
      return state;
  }
};

// Get all posts
export const getStats = state => state.pipeline.stats;
export const getStatsLoading = state => state.pipeline.status === 'loading';
export const getStatsLoaded = state => state.pipeline.status === 'loaded';
export const getStatsErrors = state => state.pipeline.errors;
export const getOpportunities = state => state.pipeline.opportunities;
export const getStages = state => state.pipeline.stages;
export const getOpportunityAmounts = state => state.pipeline.opportunityAmounts;
export const getOpportunityDefaultAmountBounds = state => state.pipeline.defaultAmountBounds;

export const getConversionGraphOpportunities = state => state.pipeline.historyGraphs.opportunities;
export const getConversionGraphLoading = state => state.pipeline.historyGraphs.status === 'loading';
export const getConversionGraphOwners = state => state.pipeline.historyGraphs.owners;
export const getConversionGraphLoaded = state => state.pipeline.historyGraphs.status === 'loaded';
export const getConversionGraphAverage = state => state.pipeline.historyGraphs.average;
export const getConversionGraphHistoricalSales = state => state.pipeline.historyGraphs.historicalSales;

export const getBenchmarkGraphBestInClassCount = state => state.pipeline.historyGraphs.bestInClassCount;
export const getBenchmarkGraphBestInClassAmount = state => state.pipeline.historyGraphs.bestInClassAmount;
export const getBenchmarkGraphOpportunities = state => state.pipeline.historyGraphs.opportunities;
export const getBenchmarkGraphLoading = state => state.pipeline.historyGraphs.status === 'loading';
export const getBenchmarkGraphOwners = state => state.pipeline.brenchmarkGraph.owners;
export const getBenchmarkGraphLoaded = state => state.pipeline.historyGraphs.status === 'loaded';
export const getBenchmarkGraphAverage = state => state.pipeline.brenchmarkGraph.average;

export const getVelocityGraphPipeline = state => state.pipeline.velocityGraph.velocityPipeline;
export const getVelocityGraphOwners = state => state.pipeline.velocityGraph.velocityOwners;
export const getVelocityGraphLoading = state => state.pipeline.status === 'loading';
export const getVelocityGraphLoaded = state => state.pipeline.status === 'loaded';

export const getProjectedGraphGlobalStats = state => state.pipeline.projectedGraph.globalStats;
export const getProjectedGraphOwnerStats = state => state.pipeline.projectedGraph.ownerStats;

// Export Reducer
export default PipelineReducer;
